import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * GALLERY タグ統合（マスタータグ＋エイリアス）管理 API。
 *
 * 同義タグ（例: NA / ナレーション / ナレ）をマスタータグに束ね、GALLERY の
 * 検索・タグ候補で同一視するためのタグテーブルを管理する。
 * 管理者（ADMIN）とマネージャー（MANAGER）のみ利用可。
 *
 * - `GET    /api/admin/gallery-tags`          グループ一覧 + 未割当タグ一覧
 * - `POST   /api/admin/gallery-tags`          マスタータグ作成
 * - `POST   /api/admin/gallery-tags/reorder`  表示順 + 階層の一括更新（D&D）
 * - `PATCH  /api/admin/gallery-tags/:id`      名前・ピン留め・エイリアス更新
 * - `DELETE /api/admin/gallery-tags/:id`      グループ削除（タグ自体は残る）
 */

export interface GalleryTagAlias {
  id: number;
  name: string;
}

export interface GalleryTagGroup {
  id: number;
  name: string;
  /**
   * 親タグ（フォルダ）の id。null は最上位。階層の深さは自由
   * （BE の安全弁として 6 段まで。循環参照は 400）。
   * 親タグでの検索は配下の子タグ（孫以深も含む）の作品まで含む。
   */
  parent_id: number | null;
  is_pinned: boolean;
  /**
   * 管理テーブルの表示順（昇順）。D&D 並び替え（reorder API）で BE が
   * 振り直す。ピン留めタグは GALLERY 初期タグクラウドにもこの順で並ぶ。
   */
  display_order: number;
  aliases: GalleryTagAlias[];
  /**
   * グループ（マスター名 + エイリアス + 配下の子タグ）のヒット件数
   * （distinct 公開作品数 + 肩書き一致ユーザー数）。
   */
  works_count: number;
}

/**
 * タグ候補の出どころ。tag = 作品の実タグ、それ以外はプロフィール由来の
 * 派生ソース（肩書き / アーティスト名 / 作品名）。
 */
export type GalleryTagSource =
  | "tag"
  | "creator_title"
  | "creator_name"
  | "work_title";

/** どのグループにも割り当てられていないタグ候補（エイリアス登録の材料）。 */
export interface UnmappedGalleryTag {
  name: string;
  /** ヒット件数（作品数。肩書き（creator_title）は該当ユーザー数）。 */
  works_count: number;
  source: GalleryTagSource;
}

export interface GetGalleryTagGroupsResponse {
  status: string;
  data: {
    groups: GalleryTagGroup[];
    unmapped_tags: UnmappedGalleryTag[];
  };
}

export interface CreateGalleryTagGroupParams {
  name: string;
  /** 親タグ id（任意の深さのタグを指定可）。省略 / null で最上位。 */
  parent_id?: number | null;
  is_pinned?: boolean;
  aliases?: string[];
}

export interface UpdateGalleryTagGroupParams {
  id: number;
  name?: string;
  /**
   * 親タグ id（任意の深さのタグを指定可。自分自身・自分の子孫は 400）。
   * null で最上位へ戻す。
   */
  parent_id?: number | null;
  is_pinned?: boolean;
  /** 指定時は全置換。 */
  aliases?: string[];
}

/** reorder API の 1 要素（並び替えと親変更を同時に確定する）。 */
export interface ReorderGalleryTagItem {
  id: number;
  /** null で最上位。 */
  parent_id: number | null;
  display_order: number;
}

export interface GalleryTagGroupResponse {
  status: string;
  data: {
    group: GalleryTagGroup;
  };
}

export const adminGalleryTagsApi = createApi({
  reducerPath: "adminGalleryTagsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["GalleryTagGroups"],
  endpoints: (builder) => ({
    getGalleryTagGroups: builder.query<GetGalleryTagGroupsResponse, void>({
      query: () => "/api/admin/gallery-tags",
      providesTags: [{ type: "GalleryTagGroups", id: "LIST" }],
    }),

    /** マスタータグ作成。display_order は BE が末尾（最大値 + 1）を自動採番する。 */
    createGalleryTagGroup: builder.mutation<
      GalleryTagGroupResponse,
      CreateGalleryTagGroupParams
    >({
      query: (body) => ({
        url: "/api/admin/gallery-tags",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "GalleryTagGroups", id: "LIST" }],
    }),

    updateGalleryTagGroup: builder.mutation<
      GalleryTagGroupResponse,
      UpdateGalleryTagGroupParams
    >({
      query: ({ id, ...body }) => ({
        url: `/api/admin/gallery-tags/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "GalleryTagGroups", id: "LIST" }],
    }),

    deleteGalleryTagGroup: builder.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/api/admin/gallery-tags/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "GalleryTagGroups", id: "LIST" }],
    }),

    /**
     * 表示順・階層の一括更新。表示順（親の直後に子）どおりの
     * `{id, parent_id, display_order}` 配列を渡すと、D&D の並び替えと
     * 階層移動を 1 リクエストで確定する。循環参照・深さ超過は 400
     * （何も変更されない）。ピン留めタグのタグクラウド表示順
     * （display_order 昇順）にもそのまま効く。
     */
    reorderGalleryTagGroups: builder.mutation<
      { status: string },
      ReorderGalleryTagItem[]
    >({
      query: (items) => ({
        url: "/api/admin/gallery-tags/reorder",
        method: "POST",
        body: { items },
      }),
      invalidatesTags: [{ type: "GalleryTagGroups", id: "LIST" }],
    }),
  }),
});

export const {
  useGetGalleryTagGroupsQuery,
  useCreateGalleryTagGroupMutation,
  useUpdateGalleryTagGroupMutation,
  useDeleteGalleryTagGroupMutation,
  useReorderGalleryTagGroupsMutation,
} = adminGalleryTagsApi;
